import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";

const NAV = [
  { label: "Services", href: "#services" },
  { label: "Standards", href: "#standards" },
  { label: "Who We Help", href: "#trust" },
  { label: "Trade Partners", href: "#trade-partners" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Contact", href: "#contact" },
];

export default function Header({ onNav }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    onNav(href);
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      data-testid="site-header"
      className={`fixed top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "border-[#2a2a2a] bg-[#050505]/85 backdrop-blur-xl"
          : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 md:px-10">
        <button
          onClick={() => go("#top")}
          data-testid="logo-home-btn"
          className="relative flex items-center"
        >
          <img
            src="/logo-mark.png"
            alt="FS Restoration Group"
            className="h-24 w-auto object-contain md:h-36"
          />
          <span className="-ml-10 mt-5 whitespace-nowrap font-display text-lg font-semibold uppercase tracking-tight text-[#f5f5f0] sm:text-xl md:-ml-16 md:mt-10 md:text-2xl">
            FS Restoration <span className="text-[#8a8a8a]">Group</span>
          </span>
        </button>

        <nav className="hidden items-center gap-9 lg:flex">
          {NAV.map((n) => (
            <button
              key={n.href}
              onClick={() => go(n.href)}
              data-testid={`nav-${n.href.slice(1)}`}
              className="link-underline text-sm font-medium text-[#8a8a8a] transition-colors hover:text-[#f5f5f0]"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="tel:0407051043"
            data-testid="emergency-call-btn"
            className="group hidden items-center gap-2 bg-[#f5f5f0] px-5 py-3 text-sm font-semibold text-[#050505] transition-colors duration-300 hover:bg-[#c4a962] sm:flex"
          >
            <Phone className="h-4 w-4" strokeWidth={2.4} />
            0407 051 043
          </a>
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-btn"
            className="flex h-11 w-11 items-center justify-center border border-[#2a2a2a] text-[#f5f5f0] lg:hidden"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-[#2a2a2a] bg-[#050505] lg:hidden"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col px-5 py-4">
              {NAV.map((n) => (
                <button
                  key={n.href}
                  onClick={() => go(n.href)}
                  className="border-b border-[#1a1a1a] py-4 text-left font-display text-lg uppercase tracking-tight text-[#f5f5f0]"
                >
                  {n.label}
                </button>
              ))}
              <a
                href="tel:0407051043"
                className="mt-4 flex items-center justify-center gap-2 bg-[#c4a962] py-4 font-semibold text-[#050505]"
              >
                <Phone className="h-4 w-4" /> Call 24/7 · 0407 051 043
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
